import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { cdcDestination, getOracleLinkedServiceControls } from 'app/registries/consts/metadata-consts';
import { IDataflowTypeMetadataSource } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { format } from 'app/shared/utils/string.util';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { oracleAdditionalPropertiesKeyOptions } from '../consts/oracle-const';
import { GlobalVariables } from 'app/services/globals';
import { ConnectionAuthKind } from 'shell-models-lib';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';

const dataflowOracleSource: IDataflowTypeMetadataSource = {
    type: DatasetFormatType.Oracle,
    settings: adfModelTypeMap.oracle.dataflowSettings,
    controls: [
        {
            type: 'radio-switch',
            bind: 'useQuery',
            label: stringResources.InputLabel,
            options: [
                {
                    label: stringResources.ModelName_Table,
                    value: false
                },
                {
                    label: stringResources.QueryLabel,
                    value: true
                }
            ],
            cases: [
                {
                    caseValue: true,
                    children: [
                        {
                            type: 'parameterizable',
                            child: {
                                type: 'textarea',
                                bind: 'query',
                                label: stringResources.QueryLabel,
                                isDataFlowExpression: true
                            },
                            dynamicContentTextClass: 'input-spaced',
                            showErrorIcon: false,
                            isDataFlowExpression: true
                        }
                    ]
                },
                {
                    caseValue: false,
                    children: [
                        {
                            type: 'dataflow-schema-table-selection',
                            bind: {
                                schemaName: 'schemaName',
                                tableName: 'tableName'
                            },
                            selectionType: ['schema', 'table'],
                            schemaRequired: true,
                            tableRequired: true
                        }
                    ]
                }
            ]
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'timeout',
                label: stringResources.Timeout,
                description: stringResources.Oracle_Timeout_Description,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        }
    ]
};

export const oracleV2LinkedServiceControls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'server',
            label: stringResources.Server,
            required: true,
            validators: [{ type: 'required', errorMessage: '' }],
            isLabelVertical: true
        }
    },
    {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        options: [
            {
                label: stringResources.Basic_DisplayText,
                value: 'Basic'
            }
        ],
        required: true,
        isLabelVertical: true,
        defaultValue: 'Basic'
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'userName',
            label: stringResources.ModelName_UserName,
            required: true,
            validators: [{ type: 'required', errorMessage: '' }],
            isLabelVertical: true
        }
    },
    {
        type: 'dynamic-akv-switcher',
        enableAKVParameterization: true,
        hideAKVSwitcherInConnStrProps: true,
        bind: 'password',
        label: stringResources.ModelName_Password,
        child: {
            type: 'parameterizable',
            attribute: 'password',
            disableAddParameter: true,
            child: {
                type: 'password',
                label: stringResources.ModelName_Password,
                required: true,
                isLabelVertical: true,
                validators: [{ type: 'required', errorMessage: '' }]
            },
            description: format(
                stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Oracle_DisplayText),
                stringResources.ModelName_Password
            )
        }
    },
    {
        type: 'parameter-config',
        bind: 'parameterizableAdditionalFields',
        label: stringResources.AdditionalConnectionStringFields,
        isLayoutVertical: true,
        isKeyDropdown: true,
        keyOptions: oracleAdditionalPropertiesKeyOptions,
        keyOptionHasType: true,
        dynamicInputTypes: true,
        dynamicInputTypesParameterizable: true,
        keyLabel: stringResources.PropertyName,
        defaultWidth: 250
    }
];

export const oracleMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const exposureControlService = await getServiceInstance<ExposureControlService>(
        injector,
        'ExposureControlService'
    );
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.oracle.linkedServiceType,
        displayName: stringResources.Oracle_DisplayText,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-Oracle'
        },
        isDeprecated: GlobalVariables.showLegacy ? undefined : '{ version !== "v2" }',
        showWarningMessage: '{ version !== "v2" }',
        SHIRVersion: {
            v2: '5.50.0.0'
        },
        warningMessage: format(
            stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
            stringResources.VersionLabel.toLocaleLowerCase()
        ),
        warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-oracle',
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        supportOPDG: GlobalVariables.enableOPDG,
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        cdc: {
            supportCDC: true,
            supportedCDCDestinations: cdcDestination,
            tableIcon: {
                regular: 'icon-table_text_cdc_20_regular',
                filled: 'icon-table_text_cdc_20_filled'
            },
            GA: true
        },
        supportMultipleIncrementalColumns: true,
        supportedAuthKindsInTrident: [ConnectionAuthKind.Basic],
        supportOPDGInMirror: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            id: BuiltInDataSourceOptionId.Oracle
        },
        supportScriptActivity: true,
        distinguishTableView: true,
        queryAlias: 'oracleReaderQuery',
        supportItemPath: true,
        isFirstClass: true,
        tableColumnNameQuotes: ['"', '"'],
        supportDatasetV2: true,
        supportPreCopyScript: true,
        copySinkTypes: [adfModelTypeMap.oracle.copySinkType],
        copySourceTypes: [adfModelTypeMap.oracle.copySourceType],
        category: ['Database'],
        timedQueryTemplate: 'select * from <<tableName>> ' +
            '\nwhere <<columnName>> >= TO_DATE(\'@{formatDateTime(pipeline().parameters.windowStart, \'yyyy-MM-dd HH:mm\' )}\', \'YYYY-MM-DD HH24:MI\') ' +
            '\nAND <<columnName>> < TO_DATE(\'@{formatDateTime(pipeline().parameters.windowEnd, \'yyyy-MM-dd HH:mm\' )}\', \'YYYY-MM-DD HH24:MI\')',
        deltaLoadQueryTemplate: `select * from <<tableName>>
        where <<columnName>> > @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue,
        concat('TO_DATE(''', formatDateTime(json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue, 'yyyy-MM-dd HH:mm'), ''', ''YYYY-MM-DD HH24:MI'')'))}
        and <<columnName>> <= @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue,
        concat('TO_DATE(''', formatDateTime(activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue, 'yyyy-MM-dd HH:mm'), ''', ''YYYY-MM-DD HH24:MI'')'))}`,
        supportFirstClassParameterExperience: true,
        controls: [
            {
                type: 'radio-switch',
                bind: 'version',
                label: stringResources.VersionLabel,
                isLabelVertical: true,
                required: true,
                options: driverVersionVersionOptions,
                defaultValue: driverVersions.v2,
                hide: GlobalVariables.showLegacy ? false : '{version === "v2" }',
                cases: [
                    {
                        caseValue: driverVersions.v1,
                        children: [
                            {
                                type: 'hint',
                                hintMode: 'warning',
                                description: format(
                                    stringResources.Connector_Legacy_IsNotSupportedWarning_alt6,
                                    stringResources.VersionLabel.toLocaleLowerCase()),
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-oracle'
                            },
                            ...getOracleLinkedServiceControls(stringResources.Oracle_DisplayText)
                        ]
                    },
                    {
                        caseValue: driverVersions.v2,
                        children: oracleV2LinkedServiceControls
                    }
                ]
            },
            ...(oracleV2LinkedServiceControls.map((control) => ({
                ...control,
                hide: GlobalVariables.showLegacy ? true : '{version !== "v2" }'
            })))
        ],
        supportDataFlowSource: () => exposureControlService.enableOracleDataFlow,
        dataflowSource: [dataflowOracleSource],
        isSHIRAllowedInDataFlow: true
    };

    return metadata;
};
